import {
  Component,
  ComponentRef,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import { UserTooltipComponent } from './user-tooltip.component';

@Component({
  selector: 'ut-dynamic-tooltip',
  templateUrl: './dynamic-tooltip.component.html',
  styleUrls: ['./dynamic-tooltip.component.scss'],
  standalone: false,
})
export class DynamicTooltipComponent implements OnDestroy {
  @Input() user: any;
  @Input() tooltipType: string = 'user';

  tooltipComponentRef?: ComponentRef<UserTooltipComponent>;
  removeTimeout?: any;
  private originalElRole?: string;
  private tooltipOriginalDescription?: string;
  private readonly delay = 200;

  constructor(
    private viewContainerRef: ViewContainerRef,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    @Inject('AccessibleInertService') private AccessibleInertService: any,
  ) {
    this.initializeAccessibility();
  }

  private initializeAccessibility(): void {
    this.AccessibleInertService.initializeInertService(this.elementRef);

    // Set role for Windows/NVDA compatibility
    const browser = (window as any).Bowser?.getParser?.(window.navigator.userAgent);
    const osName = browser?.getOSName?.();
    if (osName === 'Windows') {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'role', 'application');
    }

    this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'relative');
  }

  onMouseEnter(): void {
    this.showTooltip();
  }

  onMouseLeave(): void {
    this.hideTooltip();
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === 'Space' || event.key === ' ') {
      this.showTooltip();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  onTriggerClick(event: MouseEvent): void {
    this.showTooltip();
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  private showTooltip(): void {
    if (this.removeTimeout) {
      clearTimeout(this.removeTimeout);
      this.removeTimeout = null;
    }

    if (this.tooltipComponentRef) {
      return;
    }

    // Create the tooltip component
    this.tooltipComponentRef = this.viewContainerRef.createComponent(UserTooltipComponent);

    // Set component inputs
    this.tooltipComponentRef.instance.user = this.user;
    this.tooltipComponentRef.instance.closeTooltip.subscribe(() => {
      this.hideTooltip();
    });

    // Create wrapper div with dynamic-tooltip class
    const tooltipWrapper = this.renderer.createElement('div');
    this.renderer.addClass(tooltipWrapper, 'dynamic-tooltip');

    // Add mouse event handlers to the tooltip wrapper to keep it open when hovering
    this.renderer.listen(tooltipWrapper, 'mouseenter', () => {
      if (this.removeTimeout) {
        clearTimeout(this.removeTimeout);
        this.removeTimeout = null;
      }
    });

    this.renderer.listen(tooltipWrapper, 'mouseleave', () => {
      this.hideTooltip();
    });

    // Append the component's native element to the wrapper
    this.renderer.appendChild(tooltipWrapper, this.tooltipComponentRef.location.nativeElement);

    // Append wrapper to the host element
    this.renderer.appendChild(this.elementRef.nativeElement, tooltipWrapper);

    // Position the tooltip
    setTimeout(() => {
      this.positionTooltip(tooltipWrapper);
      this.setupFocusManagement();
    });

    // Handle accessibility
    this.handleAccessibilityOnShow();
  }

  private positionTooltip(tooltipElement: HTMLElement): void {
    if (this.isElementInViewport(tooltipElement)) {
      this.renderer.addClass(tooltipElement, 'dynamic-tooltip-position-bottom');
    } else {
      this.renderer.addClass(tooltipElement, 'dynamic-tooltip-position-top');
    }
  }

  private isElementInViewport(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0
      && rect.left >= 0
      && rect.bottom <= window.innerHeight
      && rect.right <= window.innerWidth
    );
  }

  private setupFocusManagement(): void {
    setTimeout(() => {
      this.trapAndSetFocus();
    }, 200);
  }

  private trapAndSetFocus(): void {
    const el = this.elementRef.nativeElement;
    const originalDescription = el.getAttribute('aria-label');

    if (originalDescription) {
      this.tooltipOriginalDescription = originalDescription;
      this.renderer.removeAttribute(el, 'aria-label');
      this.renderer.setAttribute(el, 'role', 'none');
    }

    const supportedHtmlTagsFocus = ['a', 'button', 'textarea', 'input', 'label'];
    const children = el.getElementsByTagName('*');
    let lastFocusElem: HTMLElement | undefined;
    let firstFocusElem: HTMLElement | undefined;

    for (let i = 0; i < children.length; i++) {
      const htmlElem = children[i] as HTMLElement;
      const childTag = htmlElem.tagName.toLowerCase();

      if (supportedHtmlTagsFocus.includes(childTag)
          && htmlElem.offsetParent !== null
          && htmlElem.getAttribute('tabindex') !== '-1') {
        lastFocusElem = htmlElem;
        if (!firstFocusElem) {
          firstFocusElem = htmlElem;
        }
      }
    }

    if (lastFocusElem && firstFocusElem) {
      this.setFocusBetweenTwoElements(lastFocusElem, firstFocusElem, false);
      this.setFocusBetweenTwoElements(firstFocusElem, lastFocusElem, true);
    }

    if (firstFocusElem) {
      setTimeout(() => {
        firstFocusElem!.focus();
      });
    }
  }

  private setFocusBetweenTwoElements(firstElem: HTMLElement, secondElem: HTMLElement, shiftPressed: boolean): void {
    firstElem.onkeydown = (event) => {
      if (event.code === 'Escape') {
        this.hideTooltip();
      }

      const shiftCorrectState = firstElem === secondElem
        || (shiftPressed && event.shiftKey)
        || (!shiftPressed && !event.shiftKey);

      if (shiftCorrectState && event.keyCode === 9) {
        setTimeout(() => {
          secondElem.focus();
        });
        event.preventDefault();
      }

      if (firstElem.tagName.toLowerCase() === 'a'
          && (event.code === 'Enter' || event.code === 'Space')) {
        (event.target as HTMLElement).click();
      }
    };
  }

  private handleAccessibilityOnShow(): void {
    const el = this.elementRef.nativeElement;

    if (el.getAttribute('role') && el.getAttribute('role') !== 'none') {
      this.originalElRole = el.getAttribute('role');
      this.renderer.setAttribute(el, 'role', 'none');
    }

    this.AccessibleInertService.setAriaHidden(true, el);
  }

  hideTooltip(): void {
    if (this.removeTimeout) {
      clearTimeout(this.removeTimeout);
    }

    // Restore accessibility attributes
    const el = this.elementRef.nativeElement;
    if (this.tooltipOriginalDescription) {
      this.renderer.setAttribute(el, 'aria-label', this.tooltipOriginalDescription);
      this.renderer.setAttribute(el, 'role', 'button');
    }

    this.removeTimeout = setTimeout(() => {
      if (this.tooltipComponentRef) {
        // Remove the tooltip wrapper
        const tooltipElement = el.querySelector('.dynamic-tooltip');
        if (tooltipElement) {
          this.renderer.removeChild(el, tooltipElement);
        }

        this.tooltipComponentRef.destroy();
        this.tooltipComponentRef = undefined;
      }
    }, this.delay);

    if (this.originalElRole) {
      this.renderer.setAttribute(el, 'role', this.originalElRole);
    }

    this.AccessibleInertService.setAriaHidden(false);
    el.focus();
  }

  ngOnDestroy(): void {
    if (this.removeTimeout) {
      clearTimeout(this.removeTimeout);
    }

    if (this.tooltipComponentRef) {
      this.tooltipComponentRef.destroy();
    }
  }
}
